/**
 * launcherThumbnailCrop — pure geometry + guard helpers for the character
 * launcher thumbnail (ENG-1079). Kept import-clean (no DOM, no gl) so it is
 * unit-testable under `node --test`; the DOM/WebGL capture lives in
 * launcherThumbnail.js, which imports these.
 */

/**
 * Append a cache-busting `?v=<version>` to a thumbnail URL (ENG-1079). The
 * thumbnail lives at a FIXED key (character-assets/{slug}/thumbnail.jpg), so a
 * re-capture uploads a new image to the same URL — and the browser keeps
 * serving the stale cached one (isaac-v6 showed a black card while the server
 * held the correct face). Busting with the character's `updatedAt` (which bumps
 * on every thumbnail write) makes each capture a new URL, so the browser fetches
 * it fresh. Not deriving the asset path — the server's URL is used verbatim, the
 * query only defeats the HTTP cache. No version → the URL unchanged.
 */
export function cacheBustedThumbnailUrl(url, version) {
  if (!url || version == null || version === '') return url
  const sep = url.includes('?') ? '&' : '?'
  return `${url}${sep}v=${encodeURIComponent(version)}`
}

/**
 * True when the CENTRE of an RGBA frame is essentially black — used to reject a
 * capture taken before the subject has painted (a splat still decoding), so
 * auto-capture retries instead of saving a subject-less thumbnail.
 *
 * The centre, not the whole frame, is the load-bearing choice (isaac-v6, a slow
 * splat): a too-early frame shows only the studio's ground grid, which adds
 * enough EDGE pixels that a whole-frame check reads as "not blank" and the grid
 * gets saved. The portrait subject sits in the centre, so a centre-box check
 * rejects a grid-only frame while passing any frame with the character in it.
 * Conservative `ratio` (near-total black) so a dark-but-populated centre passes.
 *
 * `data` is a Uint8ClampedArray of RGBA (canvas getImageData().data); width/
 * height are the frame's pixel dimensions.
 */
export function isFrameBlank(data, width, height, { luma = 14, ratio = 0.985, step = 13, box = 0.6 } = {}) {
  if (!data || data.length < 4 || !(width > 0) || !(height > 0)) return true
  const x0 = Math.floor((width * (1 - box)) / 2)
  const x1 = Math.ceil((width * (1 + box)) / 2)
  const y0 = Math.floor((height * (1 - box)) / 2)
  const y1 = Math.ceil((height * (1 + box)) / 2)
  let sampled = 0
  let dark = 0
  for (let y = y0; y < y1; y += step) {
    for (let x = x0; x < x1; x += step) {
      const i = (y * width + x) * 4
      const l = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]
      if (l < luma) dark++
      sampled++
    }
  }
  return sampled === 0 || dark / sampled >= ratio
}

// Portrait 3:4 output — a standing character reads best vertically, and the
// launcher card tiles are portrait (ENG-1079). 480x640 keeps the tile crisp
// (tiles render ~180-220px wide) without a heavy upload.
export const THUMB_W = 480
export const THUMB_H = 640

/**
 * Center crop rectangle for drawing a source canvas of size (srcW x srcH) into
 * an output of the given aspect ratio (aspect = outWidth / outHeight). Returns
 * the source rect to sample. For a PORTRAIT target (aspect < 1) on a landscape
 * viewport this is a tall centered slice — full height, so a standing avatar is
 * captured head-to-feet. A degenerate (zero-area) source returns null: the
 * caller treats that as "nothing rendered yet", never a valid capture.
 */
export function thumbnailCropRect(srcW, srcH, aspect) {
  if (!(srcW > 0) || !(srcH > 0) || !(aspect > 0)) return null
  // Prefer full height; if that would be wider than the source, fall back to
  // full width and letterbox vertically.
  let cw = srcH * aspect
  let ch = srcH
  if (cw > srcW) {
    cw = srcW
    ch = srcW / aspect
  }
  return {
    sx: Math.floor((srcW - cw) / 2),
    sy: Math.floor((srcH - ch) / 2),
    cw: Math.round(cw),
    ch: Math.round(ch),
  }
}

/**
 * The auto-fallback guard (ENG-1079): a freshly loaded character with no
 * thumbnail yet gets ONE automatic capture. Never fires when a thumbnail
 * already exists (a manual capture must never be overwritten), never before
 * the avatar reports ready (a capture then reads a blank/half-loaded scene),
 * and never twice for the same character in a session (no retry loop).
 *
 * `attempted` is the caller's set/lookup of slugs already auto-attempted.
 */
export function shouldAutoCaptureThumbnail({ slug, hasThumbnail, ready }, attempted) {
  if (!slug) return false
  if (hasThumbnail) return false
  if (!ready) return false
  if (attempted && attempted.has && attempted.has(slug)) return false
  return true
}
